import { useMutation, useQuery } from "@apollo/client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useToast } from "../../common/hooks/useToast";
import { describeError } from "../../common/rateLimit";
import { Item } from "../../styles/common";
import { Filter, UserFilters } from "../users/UserFilters";
import { WhatsAppTestMessage } from "./WhatsAppTestMessage";
import { BROADCAST_WHATSAPP, CANCEL_WHATSAPP_BROADCAST, SEND_WHATSAPP_TEST } from "./whatsapp.mutation";
import {
  WHATSAPP_AUDIENCE_COUNT,
  WHATSAPP_BROADCAST,
  WHATSAPP_BROADCASTS,
  WHATSAPP_SETTINGS,
  WHATSAPP_TEMPLATES,
} from "./whatsapp.queries";
import {
  describeProgress,
  isApproved,
  isFinished,
  MAX_TEST_PHONES,
  missingVariables,
  newIdempotencyKey,
  parsePhones,
  previewBody,
  toUserFilterInput,
  toVariablesInput,
  WhatsAppBroadcastRow,
  WhatsAppTemplate,
} from "./whatsapp";

/** Every 3s, inside the broadcast-progress tier's 60 a minute. */
const POLL_MS = 3000;

const HISTORY_VARIABLES = { pagination: { skip: 0, take: 20 } };

/**
 * Send one Meta-approved WhatsApp template to every member a filter reaches,
 * through Twilio. Backend reference: docs/WHATSAPP_BROADCAST.md in ilbp-backend.
 *
 * There is no free-text message on purpose: WhatsApp only lets a business start
 * a conversation with an approved template, and Twilio accepts free text and
 * drops it afterwards, so a text box here would look like it worked.
 */
export const WhatsAppBroadcast: React.FC = (): JSX.Element => {
  const { success, error: toastError } = useToast();

  const settingsQuery = useQuery(WHATSAPP_SETTINGS);
  const settings = settingsQuery.data?.whatsAppSettings;
  const configured = settings?.configured === true;

  const templatesQuery = useQuery(WHATSAPP_TEMPLATES, { skip: !configured });
  const templates: WhatsAppTemplate[] = useMemo(
    () => templatesQuery.data?.whatsAppTemplates ?? [],
    [templatesQuery.data]
  );

  const [filter, setFilter] = useState<ReturnType<typeof toUserFilterInput> | undefined>(undefined);
  const audienceQuery = useQuery(WHATSAPP_AUDIENCE_COUNT, { variables: { filter } });
  const audience: number | undefined = audienceQuery.data?.whatsAppAudienceCount;

  const [contentSid, setContentSid] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [phones, setPhones] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [confirming, setConfirming] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const template = templates.find((t) => t.sid === contentSid);
  const missing = missingVariables(template, values);
  const ready = configured && !!template && isApproved(template) && missing.length === 0;

  const historyQuery = useQuery(WHATSAPP_BROADCASTS, { variables: HISTORY_VARIABLES });
  const history: WhatsAppBroadcastRow[] = historyQuery.data?.whatsAppBroadcasts ?? [];

  const progressQuery = useQuery(WHATSAPP_BROADCAST, {
    variables: { id: activeId },
    skip: !activeId,
    pollInterval: POLL_MS,
    fetchPolicy: "network-only",
  });
  const active: WhatsAppBroadcastRow | undefined = progressQuery.data?.whatsAppBroadcast;
  const running = !!active && !isFinished(active.status);

  const { stopPolling } = progressQuery;
  const { refetch: refetchHistory } = historyQuery;
  useEffect(() => {
    if (active && isFinished(active.status)) {
      stopPolling();
      refetchHistory();
    }
  }, [active, stopPolling, refetchHistory]);

  const onFilter = useCallback((next: Filter) => setFilter(toUserFilterInput(next)), []);

  const onTemplate = (sid: string) => {
    setContentSid(sid);
    setValues({});
  };

  const [sendTest, testResult] = useMutation(SEND_WHATSAPP_TEST, {
    onCompleted: (data) => success(`Test sent to ${data.sendWhatsAppTest.join(", ")}.`),
    onError: (e) => toastError(describeError(e, "The test was not sent")),
  });

  const [broadcast, broadcastResult] = useMutation(BROADCAST_WHATSAPP, {
    onCompleted: (data) => {
      setActiveId(data.broadcastWhatsApp.id);
      // The next message is a new broadcast. A retry of this one would have
      // reused the key and come back with the same row.
      setIdempotencyKey(newIdempotencyKey());
      refetchHistory();
    },
    onError: (e) => toastError(describeError(e, "The broadcast did not start")),
  });

  const [cancel, cancelResult] = useMutation(CANCEL_WHATSAPP_BROADCAST, {
    onCompleted: () => {
      progressQuery.refetch();
      refetchHistory();
    },
    onError: (e) => toastError(describeError(e, "Could not cancel")),
  });

  const parsedPhones = parsePhones(phones);
  const testProblem =
    parsedPhones.phones.length > MAX_TEST_PHONES
      ? `Up to ${MAX_TEST_PHONES} numbers.`
      : parsedPhones.invalid.length
        ? `Use international format, e.g. +15551234567: ${parsedPhones.invalid.join(", ")}`
        : "";

  const onSendTest = () => {
    if (!template) return;
    sendTest({
      variables: {
        input: {
          contentSid: template.sid,
          variables: toVariablesInput(template, values),
          phones: parsedPhones.phones,
        },
      },
    });
  };

  const onConfirmBroadcast = () => {
    if (!template) return;
    setConfirming(false);
    broadcast({
      variables: {
        input: {
          contentSid: template.sid,
          variables: toVariablesInput(template, values),
          idempotencyKey,
          ...(filter ? { filter } : {}),
        },
      },
    });
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Item>
            <Typography sx={{ m: 2 }} variant="h4">Send WhatsApp</Typography>
          </Item>
        </Grid>

        <Grid item xs={12}>
          <Item sx={{ p: 2 }}>
            <WhatsAppTestMessage testSender={settings?.testSender} />
          </Item>
        </Grid>

        <Grid item xs={12}>
          <Item>
            <Stack spacing={2} sx={{ p: 1 }}>
              {settingsQuery.loading && <LinearProgress />}
              {settingsQuery.error && (
                <Alert severity="error">{describeError(settingsQuery.error, "Could not read the WhatsApp settings")}</Alert>
              )}
              {settings && !configured && (
                <Alert severity="error">
                  WhatsApp broadcasts are not set up on this server. They need a number Meta has approved as a
                  WhatsApp sender in Twilio, set as TWILIO_PHONE_NUMBER on the API. The test message above still
                  works: it comes from the Twilio Sandbox until then.
                </Alert>
              )}
              {settings && configured && settings.dryRun && (
                <Alert severity="warning">
                  This server is in dry-run mode: a broadcast counts who it would reach and sends nothing. Test
                  sends are still delivered.
                </Alert>
              )}
              {settings && configured && (
                <Typography variant="body2" color="text.secondary">Sending from {settings.sender}</Typography>
              )}

              <Accordion TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  Audience: {audience === undefined ? "…" : `${audience} member${audience === 1 ? "" : "s"}`}
                  {filter ? " (filtered)" : " (everyone)"}
                </AccordionSummary>
                <AccordionDetails>
                  <UserFilters onFilter={onFilter} />
                </AccordionDetails>
              </Accordion>
              <Typography variant="body2" color="text.secondary">
                Only members with a phone number they confirmed with a code. Deleted and banned accounts, and
                anyone marked do-not-contact in the CRM, are left out.
              </Typography>

              <TextField
                select
                label="Template"
                value={contentSid}
                onChange={(e) => onTemplate(e.target.value)}
                disabled={!configured || templatesQuery.loading}
                helperText={
                  templatesQuery.error
                    ? describeError(templatesQuery.error, "Could not load the templates")
                    : configured && !templatesQuery.loading && templates.length === 0
                      ? "There are no templates on the Twilio account yet. Create one in Twilio's Content Template Builder and submit it for WhatsApp approval."
                      : "Only templates Meta has approved can be sent."
                }
                error={!!templatesQuery.error}
              >
                {templates.map((t) => (
                  <MenuItem key={t.sid} value={t.sid} disabled={!isApproved(t)}>
                    {t.name} ({t.language}){isApproved(t) ? "" : ` — ${t.status}`}
                  </MenuItem>
                ))}
              </TextField>

              {template && (
                <>
                  {template.variables.map((key) => (
                    <TextField
                      key={key}
                      label={`{{${key}}}`}
                      value={values[key] ?? ""}
                      onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                      helperText="One line. {firstName} becomes each member's first name (“friend” when there is none)."
                    />
                  ))}
                  <Box>
                    <Typography variant="subtitle2">Preview</Typography>
                    <Typography data-testid="whatsapp-preview" sx={{ whiteSpace: "pre-wrap", p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
                      {previewBody(template, values)}
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                    <TextField
                      label="Test numbers"
                      placeholder="+12025550143, +12025550187"
                      value={phones}
                      onChange={(e) => setPhones(e.target.value)}
                      error={!!testProblem}
                      helperText={testProblem || `Up to ${MAX_TEST_PHONES}, international format. Delivered even in dry-run mode.`}
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={onSendTest}
                      disabled={!ready || parsedPhones.phones.length === 0 || !!testProblem || testResult.loading}
                    >
                      Send test
                    </Button>
                  </Stack>
                </>
              )}

              <Box>
                <Button
                  size="large"
                  variant="contained"
                  color="success"
                  onClick={() => setConfirming(true)}
                  disabled={!ready || !audience || running || broadcastResult.loading}
                >
                  {audience ? `Send to ${audience} member${audience === 1 ? "" : "s"}` : "Send"}
                </Button>
              </Box>
            </Stack>
          </Item>
        </Grid>

        {active && (
          <Grid item xs={12}>
            <Item>
              <Stack spacing={1} sx={{ p: 1 }}>
                <Typography variant="h6">
                  {active.templateName}: {active.status.toLowerCase()}
                </Typography>
                {running && (
                  <LinearProgress
                    variant={active.totalRecipients ? "determinate" : "indeterminate"}
                    value={active.totalRecipients ? ((active.sent + active.skipped + active.failed) / active.totalRecipients) * 100 : 0}
                  />
                )}
                <Typography data-testid="whatsapp-progress">{describeProgress(active)}</Typography>
                {active.error && <Alert severity="error">{active.error}</Alert>}
                <Typography variant="body2" color="text.secondary">
                  Sent means Twilio accepted the message. Whether it reached each phone shows in the Twilio
                  console.
                </Typography>
                {running && (
                  <Box>
                    <Button color="error" onClick={() => cancel({ variables: { id: active.id } })} disabled={cancelResult.loading}>
                      Cancel broadcast
                    </Button>
                  </Box>
                )}
              </Stack>
            </Item>
          </Grid>
        )}

        <Grid item xs={12}>
          <Item>
            <Typography sx={{ m: 1 }} variant="h6">Recent WhatsApp broadcasts</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Started</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Audience</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">Skipped</TableCell>
                  <TableCell align="right">Failed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>{historyQuery.loading ? "Loading…" : "None yet."}</TableCell>
                  </TableRow>
                )}
                {history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{dayjs(row.createdAt).format("YYYY-MM-DD HH:mm")}</TableCell>
                    <TableCell>{row.templateName}</TableCell>
                    <TableCell>{row.status.toLowerCase()}{row.dryRun ? " (dry run)" : ""}</TableCell>
                    <TableCell align="right">{row.totalRecipients}</TableCell>
                    <TableCell align="right">{row.sent}</TableCell>
                    <TableCell align="right">{row.skipped}</TableCell>
                    <TableCell align="right">{row.failed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Item>
        </Grid>
      </Grid>

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Send this WhatsApp message?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{template?.name}” goes to {audience} member{audience === 1 ? "" : "s"}. Each one is a paid WhatsApp
            message, and a sent message cannot be recalled.
            {settings?.dryRun ? " This server is in dry-run mode, so nothing will actually be sent." : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Back</Button>
          <Button color="success" variant="contained" onClick={onConfirmBroadcast}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
